
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import { Issue, HomepageConfig, HighlightArticle } from '../types';

import StructuredData from '../components/StructuredData';
import SEOPage from '../components/SEOPage';
import seoGlobals from '../seo_globals.json';
import { unescapeHtml } from '../utils/unescapeHtml';
import { sanitizeLimitedHtml } from '../utils/sanitizeLimitedHtml';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  const handleNavigate = (path: string) => {
    const target = path.startsWith('/') || path.startsWith('#') || path.startsWith('http') ? path : `/${path}`;
    if (target.startsWith('#')) {
      const element = document.getElementById(target.substring(1));
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else if (target.startsWith('http')) {
      window.location.href = target;
    } else {
      navigate(target);
    }
  };

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  const [currentIssue, setCurrentIssue] = useState<Issue | null>(null);
  const [highlights, setHighlights] = useState<HighlightArticle[]>([]);
  const [homeConfig, setHomeConfig] = useState<HomepageConfig | null>(null);
  const [activeFilter, setActiveFilter] = useState('All');

  // Dynamic Tags derived from loaded highlights
  const uniqueTags = Array.from(new Set(highlights.flatMap(h => h.tags || []))).sort();
  const categories = ['All', ...uniqueTags];

  // Data Fetching
  useEffect(() => {
    const handleScroll = () => setScrollY(window.pageYOffset);
    window.addEventListener('scroll', handleScroll, { passive: true });

    const init = async () => {
      try {
        await VanityAPI.bootstrap();
        const [iss, cfg] = await Promise.all([
          VanityAPI.getCurrentMagazine(),
          VanityAPI.getHomepageConfig()
        ]);

        setCurrentIssue(iss);
        setHomeConfig(cfg);

        if (iss) {
          await VanityAPI.getHighlights(iss.id).then(setHighlights);
        }
      } catch (e) {
        console.error("Home initialization failed", e);
      } finally {
        setDataLoaded(true);
      }
    };
    init();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hydration Sync: Wait for Data AND Hero Image
  useEffect(() => {
    if (dataLoaded && (heroImageLoaded || !homeConfig?.heroImageUrl)) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => setIsInitialLoading(false), 500);
      return () => clearTimeout(timer);
    }
  }, [dataLoaded, heroImageLoaded, homeConfig]);

  const filteredHighlights = highlights.filter(h => {
    if (activeFilter === 'All') return true;
    return h.tags && h.tags.includes(activeFilter);
  });

  return (
    <>
      <SEOPage
        title={homeConfig?.seoTitle || seoGlobals.seo.defaultTitle}
        description={homeConfig?.seoDescription || seoGlobals.seo.defaultDescription}
      />
      <StructuredData />
      {/* Full Screen Preloader */}
      <div
        className={`fixed inset-0 z-[9999] bg-vanity-black flex items-center justify-center transition-opacity duration-1000 ${isInitialLoading ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="text-center">
          {/* Elegant Spinner / Logo Placeholder */}
          <div className="w-16 h-16 border-4 border-vanity-pink border-t-transparent rounded-full animate-spin mb-4 mx-auto"></div>
          <span className="font-serif text-vanity-white text-xl tracking-[0.3em] font-light animate-pulse">VANITY</span>
        </div>
      </div>

      <div className={`fade-in bg-vanity-white overflow-hidden transition-opacity duration-700 ${isInitialLoading ? 'opacity-0' : 'opacity-100'}`}>
        <section className="relative h-screen w-full flex items-center justify-center overflow-hidden bg-vanity-black">
          {/* Fixed Aspect Ratio Container to Prevent CLS */}
          <div className="absolute inset-0 z-0" style={{ aspectRatio: '16/9' }}>
            {/* Dynamic Hero Image */}
            {homeConfig?.heroImageUrl || currentIssue?.coverUrl ? (
              <img
                src={homeConfig?.heroImageUrl || currentIssue?.coverUrl}
                srcSet={homeConfig?.heroVariants ? `
                   ${homeConfig.heroVariants.small} 500w,
                   ${homeConfig.heroVariants.medium} 1000w,
                   ${homeConfig.heroVariants.large} 1600w,
                   ${homeConfig.heroVariants.original} 2000w
                ` : undefined}
                sizes="100vw"
                width="1920"
                height="1080"
                fetchPriority="high" // LCP Optimization
                loading="eager"      // LCP Optimization
                alt={homeConfig?.heroAltText || currentIssue?.title || "The VANITY Lifestyle"}
                onLoad={() => setHeroImageLoaded(true)}
                className="w-full h-full object-cover will-change-transform"
                style={{
                  // Only apply parallax AFTER image loads to prevent CLS
                  transform: heroImageLoaded ? `translateY(${scrollY * 0.4}px) scale(1.15)` : 'scale(1.15)',
                  transition: heroImageLoaded ? 'transform 0.1s cubic-bezier(0.16, 1, 0.3, 1)' : 'none'
                }}
              />
            ) : null}

            <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-vanity-white via-transparent to-transparent"></div>
          </div>

          <div className="relative z-10 text-center px-4 max-w-6xl">
            {homeConfig?.heroOverline && (
              <span className="font-sans text-vanity-white text-[12px] uppercase tracking-[1em] mb-12 block opacity-80 font-bold animate-fadeInUp">
                {homeConfig.heroOverline}
              </span>
            )}

            {homeConfig?.heroTitle && (
              <h1
                className="font-serif text-vanity-white text-4xl sm:text-6xl md:text-[8rem] mb-10 md:mb-16 leading-[1] whitespace-pre-wrap animate-fadeInUp delay-200"
                dangerouslySetInnerHTML={{ __html: homeConfig.heroTitle }}
              >
              </h1>
            )}

            <div className="flex flex-col md:flex-row gap-6 md:gap-10 justify-center mt-8 md:mt-12 animate-fadeInUp delay-500">
              {homeConfig?.cta1Text && (
                <button
                  onClick={() => handleNavigate(homeConfig.cta1Url || '#latest')}
                  className="bg-vanity-pink text-white px-10 py-5 md:px-16 md:py-7 font-sans text-[12px] uppercase tracking-[0.4em] font-black hover:bg-vanity-black transition-all shadow-3xl active:scale-95"
                >
                  {homeConfig.cta1Text}
                </button>
              )}
              {homeConfig?.cta2Text && (
                <button
                  onClick={() => handleNavigate(homeConfig.cta2Url || 'contact')}
                  className="border-2 border-vanity-white text-vanity-white px-10 py-5 md:px-16 md:py-7 font-sans text-[12px] uppercase tracking-[0.4em] font-black hover:bg-vanity-white hover:text-vanity-black transition-all shadow-3xl active:scale-95"
                >
                  {homeConfig.cta2Text}
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 border-b border-vanity-charcoal/5 bg-white relative z-10">
          <div className="max-w-[1440px] mx-auto px-10">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-20 text-center items-start">
              {/* Stat 1 */}
              <div className="space-y-3">
                <h4 className="font-serif text-[40px] md:text-6xl text-vanity-black font-bold">25,000</h4>
                <p className="font-sans text-[10px] md:text-[11px] uppercase tracking-[0.5em] text-vanity-grey-dark font-black">Print-Auflage</p>
              </div>
              {/* Stat 2 */}
              <div className="space-y-3 md:border-x border-vanity-charcoal/10">
                <h4 className="font-serif text-[40px] md:text-6xl text-vanity-black italic font-bold">800+</h4>
                <p className="font-sans text-[10px] md:text-[11px] uppercase tracking-[0.5em] text-vanity-grey-dark font-black">Standorte</p>
              </div>
              {/* Stat 3 */}
              <div className="space-y-3 col-span-2 md:col-span-1 pt-8 md:pt-0 border-t md:border-t-0 border-vanity-charcoal/10">
                <h4 className="font-serif text-[40px] md:text-6xl text-vanity-pink font-bold">35+</h4>
                <p className="font-sans text-[10px] md:text-[11px] uppercase tracking-[0.5em] text-vanity-grey-dark font-black">Jahre Exzellenz</p>
              </div>
            </div>
          </div>
        </section>

        {/* CURRENT ISSUE SECTION REFACTORED */}
        <section className="py-16 md:py-40 bg-vanity-white overflow-hidden">
          {currentIssue && (
            <div className="max-w-[1440px] mx-auto px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
              <div className="lg:col-span-5 relative">
                <div className="absolute -top-10 -left-10 w-40 h-40 border border-vanity-pink/20 -z-1"></div>
                <img
                  src={currentIssue.coverUrl}
                  alt={currentIssue.coverAltText || currentIssue.title}
                  className="w-full h-auto md:w-auto md:max-h-[650px] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.3)] hover:scale-[1.02] transition-transform duration-700 cursor-pointer" />
                <div className="absolute -bottom-6 -right-6 bg-vanity-pink text-white p-6 font-serif text-xl italic shadow-lg">
                  <span className="font-sans text-[10px] font-black uppercase tracking-[0.3em]">{currentIssue.season || 'Summer'} </span>
                  <span className="font-serif text-3xl"> {currentIssue.year || '2025'} </span>
                </div>
              </div>

              {/* Right: Content & Stats */}

              <div className="lg:col-span-7">
                <span className="font-sans text-[11px] uppercase tracking-[0.5em] text-vanity-pink mb-8 block font-semibold">Highlights der Ausgabe</span><br />
                <h2 className="font-serif text-4xl md:text-6xl text-vanity-black mb-10 leading-[1.2]">
                  {currentIssue.title}
                </h2>
                <div
                  className="font-sans text-vanity-black/70 text-lg leading-relaxed mb-12 max-w-2xl"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeLimitedHtml(unescapeHtml(currentIssue.summary))
                  }}
                />

                <button
                  onClick={() => handleNavigate('/latest')}
                  className="font-sans text-[13px] uppercase tracking-[0.5em] font-black text-vanity-black border-b-[3px] border-vanity-pink pb-3 hover:opacity-70 transition-opacity inline-flex items-center gap-4"
                >
                  Jetzt reinlesen <span>&raquo;</span>
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="py-16 md:py-40 bg-vanity-charcoal min-h-screen">
          <div className="max-w-[1440px] mx-auto px-8">
            {/* HEADER WITH FILTERS */}
            <div className="mb-20 flex flex-col md:flex-row justify-between items-end gap-8 border-b border-white/5 pb-8">
              <h2 className="font-serif text-5xl md:text-7xl text-white">In dieser Ausgabe</h2>

              {/* Pill Filters */}
              <div className="flex flex-wrap gap-4">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveFilter(cat)}
                    className={`
                      px-6 py-2 rounded-full text-[10px] uppercase tracking-widest transition-all border
                      ${activeFilter === cat
                        ? 'bg-vanity-pink border-vanity-pink text-white font-bold'
                        : 'bg-transparent border-white/20 text-vanity-grey hover:border-white/40 hover:text-white'}
                    `}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* ARTICLES GRID */}
            <div className="space-y-16 md:space-y-32">
              {filteredHighlights.map((article, i) => (
                <div key={article.id} className={`grid grid-cols-1 lg:grid-cols-12 gap-16 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>

                  {/* Image Column (Span 4 - 1/3 width) */}
                  <div className={`lg:col-span-4 ${i % 2 === 1 ? 'lg:order-last' : ''}`}>
                    <div className="aspect-[3/4] relative overflow-hidden mb-6 group cursor-pointer">
                      {article.imageUrl ? (
                        <img
                          src={article.imageUrl}
                          alt={article.heroAltText || article.title}
                          loading="lazy"
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-white/5"></div>
                      )}

                      {/* Big Number Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="font-serif text-[12rem] md:text-[18rem] text-white/5 font-bold">{i + 1}</span>
                      </div>
                    </div>

                    {/* Tags Row - Under Image */}
                    {article.tags && article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-3">
                        {article.tags.map(tag => (
                          <span key={tag} className="text-[9px] uppercase tracking-[0.2em] text-vanity-grey border border-white/10 px-3 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Text Column (Span 8 - 2/3 width) */}
                  <div className="lg:col-span-8 flex flex-col justify-center space-y-8">
                    <span className="text-vanity-pink uppercase tracking-[0.5em] text-[10px] font-bold">{article.category}</span>
                    <h3 className="font-serif text-5xl md:text-6xl text-white leading-tight">{article.title}</h3>
                    <div
                      className="text-vanity-grey text-lg leading-relaxed font-light"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeLimitedHtml(unescapeHtml(article.text))
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Distribution Trust Bar */}
        <section className="py-24 border-y border-vanity-charcoal/10 bg-vanity-white">
          <div className="max-w-[1440px] mx-auto px-8 md:px-12 flex flex-col md:flex-row justify-between items-center text-center gap-12">
            <div className="max-w-xs">
              <p className="font-serif italic text-2xl text-vanity-black">"VANITY ist der Ort, an dem medizinische Expertise auf ästhetische Leidenschaft trifft."</p>
            </div>
            <div className="flex gap-16 md:gap-24">
              <div className="text-center">
                <span className="font-serif text-3xl block text-vanity-pink">Luxus</span>
                <span className="font-sans text-[9px] uppercase tracking-widest text-vanity-grey-dark">Exklusive Spas & Hotels</span>
              </div>
              <div className="text-center">
                <span className="font-serif text-3xl block text-vanity-pink">Privat</span>
                <span className="font-sans text-[9px] uppercase tracking-widest text-vanity-grey-dark">Private Fachkliniken & Ateliers</span>
              </div>
              <div className="text-center">
                <span className="font-serif text-3xl block text-vanity-pink">Exklusiv</span>
                <span className="font-sans text-[9px] uppercase tracking-widest text-vanity-grey-dark">-</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
